<template>
    <div class="form-group col-md-12 mb-4">
        <template v-if="isForeignKey">
            <label>{{ fieldName }} <span v-if="!isNullable" class="text-danger">*</span></label>
            <template v-if="!isNullable">
                <select2 :value="select2Value"
                         :plugin-options="searchOptions"
                         :is-ajax="true"
                         @input="onSelect2"
                         class="form-control" :class="{ 'is-invalid': errors.has(fieldName) && itemValue === null }" :data-vv-name="fieldName" v-validate="'required'"></select2>
                <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
            </template>

            <template v-else>
                <select2 :value="select2Value"
                         :plugin-options="searchOptions"
                         :is-ajax="true"
                         @input="onSelect2"
                         class="form-control"></select2>
            </template>
        </template>
        <template v-else>
            <template v-if="type === 'string'">
                <template v-if="isUrl">
                    <template v-if="isNullable">
                        <label>{{ fieldName }}</label>
                        <input :class="{ 'is-invalid': errors.has(fieldName) }" :data-vv-name="fieldName" v-validate="{ regex: urlPattern }"
                               v-model="itemValue" :maxlength="maxLength" type="text" class="form-control">
                        <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                    </template>

                    <template v-else>
                        <label>{{ fieldName }} <span class="text-danger">*</span></label>
                        <input :class="{ 'is-invalid': errors.has(fieldName) }" :data-vv-name="fieldName" v-validate="{ required: true, regex: urlPattern }"
                               v-model="itemValue" :maxlength="maxLength" type="text" class="form-control">
                        <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                    </template>
                </template>

                <template v-else-if="isMultiline">
                    <template v-if="isNullable">
                        <label>{{ fieldName }}</label>
                        <textarea-autosize v-model="itemValue" :maxlength="maxLength" type="text" class="form-control" :min-height="minTextareaHeight" ref="autoSizeTextArea"></textarea-autosize>
                    </template>

                    <template v-else>
                        <label>{{ fieldName }} <span class="text-danger">*</span></label>
                        <textarea-autosize :class="{ 'is-invalid': errors.has(fieldName) }" :data-vv-name="fieldName" v-validate="'required'"
                                  v-model="itemValue" :maxlength="maxLength" type="text" class="form-control" :min-height="minTextareaHeight" ref="autoSizeTextArea"></textarea-autosize>
                        <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                    </template>
                </template>

                <template v-else>
                    <template v-if="isNullable">
                        <label>{{ fieldName }}</label>
                        <input v-model="itemValue" :maxlength="maxLength" type="text" class="form-control">
                    </template>

                    <template v-else>
                        <label>{{ fieldName }} <span class="text-danger">*</span></label>
                        <input :class="{ 'is-invalid': errors.has(fieldName) }" :data-vv-name="fieldName" v-validate="'required'"
                               v-model="itemValue" :maxlength="maxLength" type="text" class="form-control">
                        <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                    </template>
                </template>
            </template>

            <template v-else-if="type === 'number'">
                <template v-if="isNullable">
                    <label>{{ fieldName }}</label>
                    <input v-numeric v-model.number.trim="itemValue" :maxlength="maxLength" type="text" class="form-control">
                </template>

                <template v-else>
                    <label>{{ fieldName }} <span class="text-danger">*</span></label>
                    <input :class="{ 'is-invalid': errors.has(fieldName) }" :data-vv-name="fieldName" v-validate="'required'"
                           v-numeric v-model.number.trim="itemValue" :maxlength="maxLength" type="text" class="form-control">
                    <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                </template>
            </template>

            <template v-else-if="type === 'bool'">
                <template v-if="isNullable">
                    <label>{{ fieldName }}</label>
                    <div>
                        <div class="form-check form-check-inline">
                            <label class="form-check-label d-flex align-items-center">
                                <input v-model="itemValue" class="form-check-input" type="radio" :name="'inlineRadioOptions' + index" :value="null" />N/A
                            </label>
                        </div>
                        <div class="form-check form-check-inline">
                            <label class="form-check-label d-flex align-items-center">
                                <input v-model="itemValue" class="form-check-input" type="radio" :name="'inlineRadioOptions' + index" :value="true" />{{ $t("common.yes") }}
                            </label>
                        </div>
                        <div class="form-check form-check-inline">
                            <label class="form-check-label d-flex align-items-center">
                                <input v-model="itemValue" class="form-check-input" type="radio" :name="'inlineRadioOptions' + index" :value="false" />{{ $t("common.no") }}
                            </label>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="form-check">
                        <label class="form-check-label">
                            <input v-model="itemValue" class="form-check-input" type="checkbox">
                            {{ fieldName }}
                        </label>
                    </div>
                </template>
            </template>

            <template v-else-if="type === 'date'">
                <template v-if="isNullable">
                    <label>{{ fieldName }}</label>
                    <div>
                        <flat-pickr class="form-control" :is-date-time-picker="!isDateOnly" v-model="itemValue"></flat-pickr>
                    </div>
                </template>

                <template v-else>
                    <label>{{ fieldName }} <span class="text-danger">*</span></label>
                    <div>
                        <flat-pickr :class="{ 'is-invalid': errors.has(fieldName) }" :is-date-time-picker="!isDateOnly" :data-vv-name="fieldName" v-validate="'required'"
                                    class="form-control" v-model="itemValue"></flat-pickr>
                        <div class="invalid-feedback" v-for="message in errorMessages">{{ message }}</div>
                    </div>
                </template>
            </template>

            <template v-else-if="type === 'select'">
                <label>{{ fieldName }} <span v-if="!isNullable" class="text-danger">*</span></label>
                <select v-model="itemValue" class="form-control">
                    <option v-if="isNullable" v-text="'null'" :value="null"></option>
                    <option v-text="s.displayName" :key="s.value" :value="s.value" v-for="s in selectValues"></option>
                </select>
            </template>
        </template>
    </div>
</template>

<script>
import "@/vue";
import i18n from "@/vue/localization";

const EditFieldComponent = {
    i18n: i18n,

    props: {
        type: {
            required: true,
            type: String
        },

        index: {
            required: true,
            type: Number
        },

        value: {
            required: false
        },

        fieldName: {
            required: true,
            type: String
        },

        columnName: {
            required: true,
            type: String
        },

        entityName: {
            required: true,
            type: String
        },

        maxLength: {
            required: false,
            type: Number
        },

        isNullable: {
            required: true,
            type: Boolean
        },

        selectValues: {
            required: true
        },

        isMultiline: {
            required: true,
            type: Boolean
        },

        isDateOnly: {
            required: true,
            type: Boolean
        },

        isForeignKey: {
            required: true,
            type: Boolean
        },

        isUrl: {
            required: true,
            type: Boolean
        },

        displayName: {
            required: true,
            type: String
        },

        isModalShown: {
            required: true,
            type: Boolean
        }
    },

    data() {
        return {
            urlPattern: /^(ftp|ftps|sftp|http|https):\/\/[\S]+$/,

            itemValue: this.value,
            select2Text: this.displayName,
            select2mounted: false,
            searchOptions: {
                minimumInputLength: 1,
                allowClear: true,
                width: "100%",
                placeholder: "-",
                ajax: {
                    url: this.url || "/api/EntityAdministration/Search",
                    delay: 250,
                    data: params => ({
                        term: params.term,
                        columnName: this.columnName,
                        entityName: this.entityName
                    }),
                    processResults: data => {
                        return {
                            results: data.map(item => ({
                                id: item.id,
                                text: item.displayName,
                                data: item
                            }))
                        };
                    }
                }
            },
            minTextareaHeight: 120
        };
    },

    methods: {
        onSelect2(event) {
            if (event) {
                this.itemValue = event.id;
                this.select2Text = event.text;
                return;
            }

            if (this.select2mounted) {
                this.itemValue = null;
            }
        },
        getErrorMessage(error) {
            const rule = error.rule;

            if (rule === "required") {
                return this.$t("editEnrollments.needFillField");
            }

            if (rule === "regex") {
                return this.$t("common.incorrectUrl");
            }

            return error.msg;
        }
    },

    mounted() {
        if (this.isForeignKey) {
            this.select2mounted = true;
        }
    },

    computed: {
        select2Value() {
            if (!this.itemValue) {
                return null;
            }

            return {
                id: this.itemValue,
                text: this.select2Text
            };
        },
        errorMessages() {
            return this.$validator.errors.items
                .filter(item => item.field === this.fieldName)
                .map(e => this.getErrorMessage(e));
        }
    },

    watch: {
        itemValue() {
            if (this.itemValue !== this.value) {
                this.$emit("input", this.itemValue);
            }
        },

        value() {
            if (this.itemValue !== this.value) {
                this.itemValue = this.value;
            }
        },

        validate() {
            return this.$validator.validateAll();
        },

        isModalShown() {
            if (this.isModalShown && this.$refs.autoSizeTextArea) {
                this.$refs.autoSizeTextArea.resize();
            }
        }
    }
};

export default EditFieldComponent;
</script>
